import { useCallback, useRef, useState } from 'react';

/**
 * One step of undo for the things that REPLACE a compose field wholesale — inserting a
 * template, or accepting a generated draft.
 *
 * Those are the only edits a person cannot take back. Typing is covered by the
 * browser's own undo stack, but a programmatic write is not part of it: React setting
 * `value` on a textarea, or code assigning innerHTML, leaves nothing for Ctrl+Z to pop.
 * So someone who wrote a message, then opened Use Template to look at the options, lost
 * what they had written with no way back.
 *
 * Deliberately one step, not a history. What it protects against is a single
 * destructive action, and a stack would then have to decide how it interleaves with the
 * native one — which is a question with no good answer.
 *
 * `canUndo` goes false as soon as the field is edited again: by then the replacement is
 * not what is on screen any more, and restoring the older text would throw away work
 * the person did on purpose. That is also what keeps the Ctrl+Z handler honest — it
 * only claims the keystroke while there is a replacement standing untouched, and
 * otherwise lets it through to the browser.
 */
export interface IReplaceUndo<T> {
  /** Write `next` into the field, remembering what was there. */
  replace: (next: T) => void;
  /** Put the previous content back. No-op when there is nothing to undo. */
  undo: () => void;
  canUndo: boolean;
  /** Attach to the field: claims Ctrl/Cmd+Z only while `canUndo`. */
  onKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * @param isEqual How to tell "still the replacement" from "edited since". The default
 *   suits a plain string. An email step replaces its subject and its body in one go, so
 *   it tracks both as one object and has to compare them field by field — with `Object.is`
 *   a fresh object would never match and Undo could never light up.
 */
export function useReplaceUndo<T>(
  current: T,
  write: (value: T) => void,
  isEqual: (a: T, b: T) => boolean = Object.is,
): IReplaceUndo<T> {
  const snapshot = useRef<{ before: T; after: T } | null>(null);
  // State, not just the ref, so the Undo button re-renders when it becomes available.
  const [, bump] = useState(0);

  const replace = useCallback((next: T) => {
    snapshot.current = { before: current, after: next };
    write(next);
    bump(n => n + 1);
  }, [current, write]);

  const undo = useCallback(() => {
    const snap = snapshot.current;
    if (!snap) return;
    snapshot.current = null;
    write(snap.before);
    bump(n => n + 1);
  }, [write]);

  const canUndo = !!snapshot.current && isEqual(snapshot.current.after, current);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.shiftKey) return;
    if (e.key !== 'z' && e.key !== 'Z') return;
    if (!snapshot.current || !isEqual(snapshot.current.after, current)) return;
    e.preventDefault();
    undo();
  }, [current, undo, isEqual]);

  return { replace, undo, canUndo, onKeyDown };
}

export default useReplaceUndo;
